import {
  useCurrentWorkspace,
  useFindAssetIdForWorkspace,
} from '@gripp/shared-logic';
import {
  LoadingIndicator,
  SlideAction,
  SlideLayout,
  ViewMode,
  type ColumnData,
  type SlideLayoutLocationState,
} from '@gripp/shared-ui';
import { useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ActivityEditLabels from '../../activity/components/activity-edit-label';
import { AssetRouteProvider } from '../../assets/assetRouteContext';
import { AssetCard } from '../../assets/components/assetCard';
import { AssetFilters } from '../../assets/components/assetFilters';
import { NewIssueBody } from '../../issues';
import { HeaderResetButton } from '../../shared/components/headerResetButton';

export const WorkspaceNewIssue = () => {
  const { workspaceId = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { assetId, isLoading } = useFindAssetIdForWorkspace(workspaceId);
  const [selectedLabels, setSelectedLabels] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [isLabelsPanelOpen, setIsLabelsPanelOpen] = useState(false);
  const currentWorkspace = useCurrentWorkspace();
  const resetLabelsRef = useRef<(() => void) | undefined>(undefined);
  const locationState = location.state as SlideLayoutLocationState | null;
  const initialTransition =
    locationState?.slideLayoutInitialTransition === SlideAction.NEXT ||
    locationState?.slideLayoutInitialTransition === SlideAction.PREV
      ? locationState.slideLayoutInitialTransition
      : undefined;

  const basePath = `/workspaces/${workspaceId}`;
  const routeContext = {
    basePath,
    listPath: '/workspaces',
    isWorkspaceMode: true,
  };

  const leftColumn: ColumnData = {
    title: 'admin.menu.workspaces',
    mainComponent: isLoading ? (
      <LoadingIndicator />
    ) : assetId ? (
      <AssetRouteProvider value={routeContext}>
        <AssetCard assetId={assetId} />
      </AssetRouteProvider>
    ) : null,
    headerComponent:
      !isLoading && assetId ? (
        <AssetRouteProvider value={routeContext}>
          <AssetFilters assetId={assetId} />
        </AssetRouteProvider>
      ) : null,
    showBackButton: true,
    onBackButtonPress: () => {
      navigate(basePath, {
        state: { slideLayoutInitialTransition: SlideAction.PREV },
      });
    },
  };

  const rightColumn: ColumnData = {
    title: 'asset.header.buttons.addSomething',
    mainComponent: isLoading ? (
      <LoadingIndicator />
    ) : assetId ? (
      <NewIssueBody
        assetId={assetId}
        workspaceId={currentWorkspace?.id}
        isOpened={true}
        modalType="sheet"
        onComplete={() =>
          navigate(basePath, {
            state: { slideLayoutInitialTransition: SlideAction.PREV },
          })
        }
        onCancel={() =>
          navigate(basePath, {
            state: { slideLayoutInitialTransition: SlideAction.PREV },
          })
        }
        onIssueCreated={(issueId) =>
          navigate(`${basePath}/activity/${issueId}`)
        }
        selectedLabels={selectedLabels}
        onLabelsChange={setSelectedLabels}
        onSelectorEdit={() => setIsLabelsPanelOpen(true)}
      />
    ) : null,
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent',
    onBackButtonPress: () => {
      navigate(basePath, {
        state: { slideLayoutInitialTransition: SlideAction.PREV },
      });
    },
    disableScroll: true,
  };

  const thirdColumn: ColumnData | undefined = isLabelsPanelOpen
    ? {
        title: 'asset.groups',
        mainComponent: (
          <ActivityEditLabels
            selectedLabels={selectedLabels ?? []}
            onLabelsChange={setSelectedLabels}
            resetRef={resetLabelsRef}
          />
        ),
        headerComponent: (
          <View style={styles.headerResetButton}>
            <HeaderResetButton onReset={() => resetLabelsRef.current?.()} />
          </View>
        ),
        showBackButton: true,
        onBackButtonPress: () => setIsLabelsPanelOpen(false),
        disableScroll: true,
      }
    : undefined;

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      thirdColumn={thirdColumn}
      viewMode={ViewMode.DUAL}
      initialTransition={initialTransition}
    />
  );
};

const styles = StyleSheet.create({
  headerResetButton: {
    marginRight: 8,
  },
});
